import { useEffect, useRef, useState } from "react";

import type { FieldDef, UploadResponse } from "../lib/api";
import { cn } from "../lib/cn";

interface FileDropzoneProps {
  def: FieldDef;
  // Upload id currently held by the parent form for this field, or "" when
  // empty. The component owns the in-flight upload state internally.
  value: string;
  // Called with the new upload id on successful upload, or "" when the
  // operator removes the file before saving.
  onChange: (uploadId: string) => void;
  // Connector id is required so the server can validate the uploaded
  // filename against the connector's `accept` allowlist server-side.
  connectorId: string;
  // Filename + size hydrated from a previous render (e.g. operator hits
  // browser-back to an in-progress dialog). Optional; the component reads
  // these straight from the upload response in the common path.
  initialMeta?: { filename: string; sizeBytes: number; sha256: string };
}

type UploadState =
  | { kind: "empty" }
  | { kind: "uploading"; filename: string; pct: number }
  | { kind: "done"; meta: UploadResponse }
  | { kind: "error"; msg: string };

// FileDropzone is the schema-driven file input for FieldFile fields. It
// answers the question "how does the operator's laptop get a file into the
// agent's container?" — drag-and-drop or click to browse, multipart POST
// to /api/v1/uploads, store the resulting id back into the form state.
export function FileDropzone({ def, value, onChange, connectorId, initialMeta }: FileDropzoneProps) {
  const [state, setState] = useState<UploadState>(() => {
    if (value && initialMeta) {
      return {
        kind: "done",
        meta: { id: value, filename: initialMeta.filename, sizeBytes: initialMeta.sizeBytes, sha256: initialMeta.sha256 },
      };
    }
    if (value) {
      // Hydrate metadata after mount (parent didn't supply it). We render
      // a thin placeholder until the GET completes.
      return { kind: "done", meta: { id: value, filename: "(loaded file)", sizeBytes: 0, sha256: "" } };
    }
    return { kind: "empty" };
  });
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // When the parent hands us an upload_id without metadata (e.g. the Edit
  // Source dialog hydrating from existing config), fetch /uploads/{id} so
  // the tile shows the real filename + size instead of the placeholder.
  useEffect(() => {
    if (state.kind !== "done") return;
    if (state.meta.sizeBytes > 0) return;
    if (!state.meta.id) return;
    let cancelled = false;
    fetch(`/api/v1/uploads/${encodeURIComponent(state.meta.id)}`, {
      credentials: "same-origin",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((meta: UploadResponse | null) => {
        if (cancelled || !meta) return;
        setState({ kind: "done", meta });
      })
      .catch(() => {
        /* leave the placeholder; orphan janitor or operator can clean up */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.kind, state.kind === "done" ? state.meta.id : ""]);

  function pickFile() {
    inputRef.current?.click();
  }

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const file = files[0];

    // Client-side accept check — surfaces the error before the upload even
    // starts. The server re-validates anyway.
    if (def.accept && def.accept.length > 0) {
      const lower = file.name.toLowerCase();
      const ok = def.accept.some((ext) => lower.endsWith(ext.toLowerCase()));
      if (!ok) {
        setState({
          kind: "error",
          msg: `File type not allowed. Expected ${def.accept.join(", ")}.`,
        });
        return;
      }
    }

    const xhr = new XMLHttpRequest();
    const url = `/api/v1/uploads?connectorId=${encodeURIComponent(connectorId)}&fieldKey=${encodeURIComponent(def.key)}`;
    xhr.open("POST", url);
    xhr.responseType = "json";
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) {
        const pct = Math.round((e.loaded / e.total) * 100);
        setState({ kind: "uploading", filename: file.name, pct });
      }
    });
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const meta = xhr.response as UploadResponse;
        setState({ kind: "done", meta });
        onChange(meta.id);
      } else {
        const errMsg =
          (xhr.response && typeof xhr.response === "object" && "error" in xhr.response
            ? (xhr.response as { error: string }).error
            : `Upload failed (${xhr.status})`) || "Upload failed";
        setState({ kind: "error", msg: errMsg });
      }
    });
    xhr.addEventListener("error", () => {
      setState({ kind: "error", msg: "Network error during upload." });
    });

    setState({ kind: "uploading", filename: file.name, pct: 0 });
    const fd = new FormData();
    fd.append("file", file);
    xhr.send(fd);
  }

  async function removeUpload() {
    if (state.kind !== "done") {
      setState({ kind: "empty" });
      onChange("");
      return;
    }
    const id = state.meta.id;
    // Best effort — if the DELETE fails, the orphan janitor will clean up.
    try {
      await fetch(`/api/v1/uploads/${id}`, { method: "DELETE", credentials: "same-origin" });
    } catch {
      /* ignore */
    }
    setState({ kind: "empty" });
    onChange("");
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <div
      onDragEnter={(e) => {
        e.preventDefault();
        setDragActive(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragActive(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setDragActive(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragActive(false);
        handleFiles(e.dataTransfer.files);
      }}
      className={cn(
        "rounded-md border-2 border-dashed bg-paper px-4 py-5 transition",
        dragActive ? "border-brand-500 bg-brand-50/40" : "border-rule",
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={def.accept?.join(",")}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      {state.kind === "empty" ? (
        <button
          type="button"
          onClick={pickFile}
          className="block w-full text-left"
        >
          <div className="text-[13px] text-ink">
            Drop a file here, or <span className="text-brand-700 underline">click to browse</span>
          </div>
          {def.accept && def.accept.length > 0 ? (
            <FileFormatHint accept={def.accept} />
          ) : null}
        </button>
      ) : null}

      {state.kind === "uploading" ? (
        <div>
          <div className="flex items-center justify-between text-[13px] text-ink">
            <span className="truncate">{state.filename}</span>
            <span className="font-mono text-[11px] text-ash">{state.pct}%</span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-rule">
            <div
              className="h-full bg-brand-600 transition-[width]"
              style={{ width: `${state.pct}%` }}
            />
          </div>
        </div>
      ) : null}

      {state.kind === "done" ? (
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-semibold text-ink">{state.meta.filename}</div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono text-[10.5px] text-ash">
              {state.meta.sizeBytes > 0 ? <span>{formatBytes(state.meta.sizeBytes)}</span> : null}
              {state.meta.sha256 ? <span>sha256 {state.meta.sha256.slice(0, 12)}…</span> : null}
            </div>
          </div>
          <div className="flex shrink-0 gap-1.5">
            <button
              type="button"
              onClick={pickFile}
              className="rounded border border-rule bg-surface px-2 py-1 text-[11px] text-ink hover:border-ink/30"
            >
              Replace
            </button>
            <button
              type="button"
              onClick={removeUpload}
              className="rounded border border-rule bg-surface px-2 py-1 text-[11px] text-rose-700 hover:border-rose-500/50"
            >
              Remove
            </button>
          </div>
        </div>
      ) : null}

      {state.kind === "error" ? (
        <div>
          <div className="text-[13px] text-rose-700">{state.msg}</div>
          <button
            type="button"
            onClick={pickFile}
            className="mt-2 rounded border border-rule bg-surface px-2 py-1 text-[11px] text-ink hover:border-ink/30"
          >
            Try another file
          </button>
        </div>
      ) : null}
    </div>
  );
}

const FORMAT_PREVIEW = 4;

export function FileFormatHint({ accept }: { accept: string[] }) {
  const [expanded, setExpanded] = useState(false);

  if (!accept || accept.length === 0) return null;

  if (accept.length <= FORMAT_PREVIEW) {
    return (
      <p className="mt-1.5 font-mono text-[11px] text-ash">
        Accepts {accept.join(" · ")}
      </p>
    );
  }

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1.5 font-mono text-[11px] text-ash hover:text-ink"
      >
        {expanded ? (
          <>
            <span>{accept.length} formats supported</span>
            <ChevronIcon dir="up" />
          </>
        ) : (
          <>
            <span>Accepts {accept.slice(0, FORMAT_PREVIEW).join(" · ")}</span>
            <span className="text-[10.5px] text-brand-600">+{accept.length - FORMAT_PREVIEW} more</span>
            <ChevronIcon dir="down" />
          </>
        )}
      </button>
      {expanded && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {accept.map((ext) => (
            <span
              key={ext}
              className="rounded border border-rule bg-paper px-1.5 py-0.5 font-mono text-[10px] text-ash"
            >
              {ext}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ChevronIcon({ dir }: { dir: "up" | "down" }) {
  return (
    <svg
      viewBox="0 0 10 6"
      width="8"
      height="6"
      fill="none"
      aria-hidden
      className={cn("shrink-0 transition-transform", dir === "up" && "rotate-180")}
    >
      <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
